import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3 } from 'lucide-react';
import { CollapsibleCard, CollapseControl } from './CollapsibleCard';
import { getPercentile, getScoreInterpretation, calculateDiscrepancies } from '../utils/helpers';
import { domainColors, domainColorsStrong, domainColorAlpha, compositeNames } from '../utils/constants';

const shortCompositeNames: Record<string, string> = {
  vci: 'VCI — Słowne',
  vsi: 'VSI — Przestrz.',
  fri: 'FRI — Płynne',
  wmi: 'WMI — Pamięć',
  psi: 'PSI — Szybkość',
  fsiq: 'FSIQ — Pełny IQ'
};


interface CognitiveProfileAnalyzerProps {
  compositeScores: Record<string, string>;
  collapse?: CollapseControl;
}

export const CognitiveProfileAnalyzer: React.FC<CognitiveProfileAnalyzerProps> = ({
  compositeScores,
  collapse,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<{
    key: string;
    name: string;
    score: number;
    percentile: number;
    interpretation: string;
    color: string;
    x: number;
    y: number;
    clientX: number;
    clientY: number;
  } | null>(null);

  // Animate points in after mount
  const [pointsVisible, setPointsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setPointsVisible(true), 100);
    const t2 = setTimeout(() => setCardsVisible(true), 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Dismiss tooltip on scroll anywhere on the page (capture phase scroll)
  useEffect(() => {
    if (!hoveredPoint) return;
    const handleScroll = () => {
      setHoveredPoint(null);
    };
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [hoveredPoint]);

  // SVG dimensions
  const svgWidth = 600;
  const svgHeight = 220;
  const paddingLeft = 50;
  const paddingRight = 50;
  const plotWidth = svgWidth - paddingLeft - paddingRight;
  const baselineY = svgHeight - 40;
  const peakHeight = 140;

  // Normal distribution params
  const mean = 100;
  const sd = 15;
  const minScore = 40;
  const maxScore = 160;

  // Helper to map score to SVG X coordinate
  const getX = (score: number) => {
    const clamped = Math.max(minScore, Math.min(maxScore, score));
    return paddingLeft + ((clamped - minScore) / (maxScore - minScore)) * plotWidth;
  };

  // Helper to map score to SVG Y coordinate
  const getY = (score: number) => {
    const exponent = -Math.pow(score - mean, 2) / (2 * Math.pow(sd, 2));
    return baselineY - peakHeight * Math.exp(exponent);
  };

  // Generate path for the main curve
  const generateCurvePath = (start: number, end: number) => {
    const points: string[] = [];
    for (let s = start; s <= end; s += 1) {
      points.push(`${getX(s)},${getY(s)}`);
    }
    return points.join(' L ');
  };

  const curveD = `M ${generateCurvePath(minScore, maxScore)}`;

  // Generate paths for shaded regions
  const getRegionPath = (start: number, end: number) => {
    const curvePoints = generateCurvePath(start, end);
    return `M ${getX(start)},${baselineY} L ${curvePoints} L ${getX(end)},${baselineY} Z`;
  };

  // SD Markers
  const sdMarkers = [55, 70, 85, 100, 115, 130, 145];
  const sdLabels: Record<number, string> = {
    55: '-3 SD',
    70: '-2 SD',
    85: '-1 SD',
    100: 'Średnia',
    115: '+1 SD',
    130: '+2 SD',
    145: '+3 SD'
  };

  // Filter valid scores to plot (memoized to keep reference stable)
  const plottedPoints = useMemo(() => {
    return Object.entries(compositeScores)
      .map(([key, valStr]) => {
        const score = parseInt(valStr);
        if (isNaN(score) || score <= 0) return null;
        const pct = getPercentile(score, 'composite');
        return {
          key,
          name: compositeNames[key] || key.toUpperCase(),
          score,
          percentile: pct || 0,
          interpretation: getScoreInterpretation(score, 'composite'),
          color: domainColors[key] || '#EC4899',
          x: getX(score),
          y: getY(score)
        };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);
  }, [compositeScores]);

  // Sync hoveredPoint details and DOM position when plottedPoints changes
  useEffect(() => {
    if (!hoveredPoint) return;

    // Find the current point state
    const currentPt = plottedPoints.find(p => p.key === hoveredPoint.key);
    if (!currentPt) {
      setHoveredPoint(null);
      return;
    }

    // Update hoveredPoint data and client coordinates
    const element = document.getElementById(`gauss-point-${currentPt.key}`);
    if (element) {
      const rect = element.getBoundingClientRect();
      const clientX = rect.left + rect.width / 2;
      const clientY = rect.top + rect.height / 2;

      setHoveredPoint(prev => {
        if (!prev) return null;
        if (
          prev.score === currentPt.score &&
          prev.percentile === currentPt.percentile &&
          prev.interpretation === currentPt.interpretation &&
          prev.clientX === clientX &&
          prev.clientY === clientY
        ) {
          return prev;
        }
        return {
          ...prev,
          ...currentPt,
          clientX,
          clientY
        };
      });

      // Poll coordinates while transitioning
      let frameId: number;
      const startTime = Date.now();
      const duration = 300;

      const updatePos = () => {
        const el = document.getElementById(`gauss-point-${currentPt.key}`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cX = r.left + r.width / 2;
        const cY = r.top + r.height / 2;

        setHoveredPoint(prev => {
          if (!prev || prev.key !== currentPt.key) return prev;
          if (prev.clientX === cX && prev.clientY === cY) return prev;
          return {
            ...prev,
            clientX: cX,
            clientY: cY
          };
        });

        if (Date.now() - startTime < duration) {
          frameId = requestAnimationFrame(updatePos);
        }
      };

      frameId = requestAnimationFrame(updatePos);
      return () => cancelAnimationFrame(frameId);
    } else {
      setHoveredPoint(prev => {
        if (!prev) return null;
        if (
          prev.score === currentPt.score &&
          prev.percentile === currentPt.percentile &&
          prev.interpretation === currentPt.interpretation
        ) {
          return prev;
        }
        return {
          ...prev,
          ...currentPt
        };
      });
    }
  }, [plottedPoints, hoveredPoint?.key]);


  const discrepancies = calculateDiscrepancies(compositeScores);
  const significantDiscrepancies = discrepancies.filter(d => d.isSignificant);

  return (
    <CollapsibleCard
      title="Profil Poznawczy i Rozkład Normalny IQ"
      icon={BarChart3}
      iconClassName="text-accent-strong"
      open={collapse?.open}
      onOpenChange={collapse?.onOpenChange}
      contentClassName="flex flex-col gap-6"
    >

      <p className="text-xs text-txt-secondary">
        Wizualizacja wyników na krzywej Gaussa (M=100, SD=15). Najedź na punkty, aby zobaczyć szczegóły.
      </p>

      {/* SVG Chart Container */}
      <div className="relative w-full bg-surface-subtle border border-brd rounded-xl p-4 flex flex-col gap-4 justify-center items-center" style={{ overflow: 'visible' }}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-auto select-none overflow-visible"
        >
          <defs>
            {/* Glow effect for plotted points */}
            <filter id="point-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            {/* Curve gradient — Warm paper: akcent szmaragdowy przechodzący w fiolet FRI */}
            <linearGradient id="curve-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--accent)" />
              <stop offset="100%" stopColor="var(--domain-fri)" />
            </linearGradient>
          </defs>

          {/* Shaded Regions underneath curve - opacities lowered for light theme */}
          {/* Very Low (<70) */}
          <path d={getRegionPath(minScore, 70)} fill="var(--domain-psi)" fillOpacity="0.04" />
          {/* Borderline (70-84) */}
          <path d={getRegionPath(70, 84)} fill="var(--domain-wmi)" fillOpacity="0.03" />
          {/* Average (85-114) */}
          <path d={getRegionPath(85, 114)} fill="var(--domain-vci)" fillOpacity="0.02" />
          {/* High (115-129) */}
          <path d={getRegionPath(115, 129)} fill="var(--domain-vsi)" fillOpacity="0.03" />
          {/* Very High (>=130) */}
          <path d={getRegionPath(130, maxScore)} fill="var(--domain-fri)" fillOpacity="0.04" />

          {/* Region Borders / Dividers */}
          {[70, 85, 115, 130].map(val => (
            <line
              key={val}
              x1={getX(val)}
              y1={baselineY}
              x2={getX(val)}
              y2={getY(val)}
              stroke="var(--border)"
              strokeDasharray="3 3"
              strokeWidth="1"
            />
          ))}

          {/* SD Grid Lines & Labels */}
          {sdMarkers.map(val => {
            const labelX = getX(val);
            return (
              <g key={val} className="opacity-60 hover:opacity-100 transition-opacity duration-200">
                <line
                  x1={labelX}
                  y1={baselineY}
                  x2={labelX}
                  y2={baselineY + 6}
                  stroke="var(--border-strong)"
                  strokeWidth="1.5"
                />
                <text
                  x={labelX}
                  y={baselineY + 20}
                  fill="var(--text-secondary)"
                  fontSize="10"
                  textAnchor="middle"
                  fontWeight="500"
                >
                  {val}
                </text>
                <text
                  x={labelX}
                  y={baselineY + 32}
                  fill="var(--text-tertiary)"
                  fontSize="8"
                  textAnchor="middle"
                >
                  {sdLabels[val]}
                </text>
              </g>
            );
          })}

          {/* Baseline */}
          <line
            x1={paddingLeft}
            y1={baselineY}
            x2={svgWidth - paddingRight}
            y2={baselineY}
            stroke="var(--border-strong)"
            strokeWidth="1.5"
          />

          {/* Main Bell Curve path */}
          <path
            d={curveD}
            fill="none"
            stroke="url(#curve-grad)"
            strokeWidth="3.5"
            strokeLinecap="round"
            className="drop-shadow-[0_2px_6px_rgba(5,150,105,0.15)]"
          />

          {/* User Score Points plotted on the curve */}
          {plottedPoints.map((pt, idx) => (
            <g
              key={pt.key}
              id={`gauss-point-${pt.key}`}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clientX = rect.left + rect.width / 2;
                const clientY = rect.top + rect.height / 2;
                setHoveredPoint({
                  ...pt,
                  clientX,
                  clientY
                });
              }}
              onMouseLeave={() => setHoveredPoint(null)}
              className="cursor-pointer"
              style={{
                transition: `transform 0.4s cubic-bezier(.34,1.56,.64,1) ${idx * 80}ms, opacity 0.4s ease ${idx * 80}ms`,
                transform: pointsVisible ? 'scale(1)' : 'scale(0)',
                transformOrigin: `${pt.x}px ${pt.y}px`,
                opacity: pointsVisible ? 1 : 0
              }}
            >
              {/* Invisible large target for easier hovering */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={24}
                fill="transparent"
                pointerEvents="all"
                style={{ transition: 'cx 0.3s ease-out, cy 0.3s ease-out' }}
              />
              {/* Outer halo */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={10}
                fill={pt.color}
                fillOpacity="0.15"
                style={{ transition: 'cx 0.3s ease-out, cy 0.3s ease-out' }}
              />
              {/* Solid point */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={5}
                fill="var(--surface-card)"
                stroke={pt.color}
                strokeWidth="2.5"
                filter="url(#point-glow)"
                style={{ transition: 'cx 0.3s ease-out, cy 0.3s ease-out' }}
              />
            </g>
          ))}
        </svg>

        {/* Legend for Gauss Curve Regions — kolory ze wspólnych tokenów domenowych */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2 pt-3 border-t border-brd w-full text-[11px]">
          {[
            { color: domainColors.psi, label: <>Bardzo niski (&lt;70)</> },
            { color: domainColors.wmi, label: <>Graniczny (70–84)</> },
            { color: domainColors.vci, label: <>Przeciętny (85–114)</> },
            { color: domainColors.vsi, label: <>Ponadprzeciętny (115–129)</> },
            { color: domainColors.fri, label: <>Bardzo wysoki (&ge;130)</> },
          ].map(({ color, label }, idx) => (
            <div key={idx} className="flex items-center gap-1.5 text-txt-secondary font-medium">
              <span
                className="w-3.5 h-3.5 rounded border inline-block"
                style={{
                  backgroundColor: domainColorAlpha(color, 10),
                  borderColor: domainColorAlpha(color, 35),
                }}
              />
              <span>{label}</span>
            </div>
          ))}
        </div>

        {/* Tooltip Portal */}
        {hoveredPoint &&
          createPortal(
            (() => {
              const viewportWidth = window.innerWidth;
              const tooltipWidth = 260;
              const isUpperHalf = hoveredPoint.clientY < window.innerHeight / 2;
              const padding = 16;
              const minX = tooltipWidth / 2 + padding;
              const maxX = viewportWidth - (tooltipWidth / 2 + padding);
              const clampedX = Math.min(Math.max(hoveredPoint.clientX, minX), maxX);
            const arrowOffset = hoveredPoint.clientX - clampedX;
            const maxArrowOffset = tooltipWidth / 2 - 16;
            const clampedArrowOffset = Math.min(Math.max(arrowOffset, -maxArrowOffset), maxArrowOffset);

            return (
              <div
                className="pointer-events-none"
                style={{
                  position: 'fixed',
                  left: `${clampedX}px`,
                  top: `${hoveredPoint.clientY}px`,
                  transform: isUpperHalf ? 'translateX(-50%)' : 'translateX(-50%) translateY(-100%)',
                  zIndex: 9999,
                }}
              >
                {/* Tooltip: nieprzezroczyste tanie tło zamiast glass-panel —
                    element position:fixed, backdrop-filter zbędnie kosztuje (I-1). */}
                <div
                  className="p-3 rounded-xl border border-brd shadow-[var(--shadow-float)] bg-surface-card text-xs flex flex-col gap-1 w-[260px] relative"
                  style={{
                    marginTop: isUpperHalf ? '12px' : '0px',
                    marginBottom: isUpperHalf ? '0px' : '12px',
                    animation: 'tooltipSpringInner 0.2s cubic-bezier(.34,1.56,.64,1) forwards',
                  }}
                >
                  {/* Arrow indicator pointing to point */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{
                      left: `calc(50% + ${clampedArrowOffset}px)`,
                      ...(isUpperHalf
                        ? { top: '-6px', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid var(--border)' }
                        : { bottom: '-6px', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid var(--border)' }
                      )
                    }}
                  />
                  <div className="font-semibold text-txt-primary flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                      style={{ backgroundColor: hoveredPoint.color }}
                    />
                    <span className="whitespace-normal leading-tight">{hoveredPoint.name}</span>
                  </div>
                  <div className="text-txt-secondary flex justify-between gap-3 mt-0.5">
                    <span>Wynik: <strong className="text-txt-primary">{hoveredPoint.score}</strong></span>
                    <span>Centyl: <strong className="text-accent-strong">{hoveredPoint.percentile}%</strong></span>
                  </div>
                  <div className="text-txt-secondary italic mt-0.5 border-t border-brd pt-1 text-[11px]">
                    Klasyfikacja: {hoveredPoint.interpretation}
                  </div>
                </div>
              </div>
            );
          })(),
          document.body
        )}

      </div>

      {/* Discrepancy Warnings */}
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold text-txt-secondary flex items-center gap-2">
          <svg className="w-4 h-4 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Analiza Statystyczna Rozbieżności
        </h4>

        {plottedPoints.length < 2 ? (
          <div className="text-xs text-txt-secondary italic p-3 border border-brd bg-surface-subtle rounded-xl text-center">
            Wprowadź co najmniej 2 wskaźniki złożone, aby wyliczyć rozbieżności psychometryczne.
          </div>
        ) : significantDiscrepancies.length > 0 ? (
          <div className="flex flex-col gap-2">
            {significantDiscrepancies.map((disc, idx) => (
              <div
                key={idx}
                className="p-3 border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/40 rounded-xl text-xs text-txt-secondary flex items-start gap-2.5 animate-fadeIn shadow-[0_2px_10px_rgba(245,158,11,0.01)]"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0 animate-pulse" />
                <div>
                  <span className="font-semibold text-amber-700 dark:text-amber-300">Różnica: {disc.diff} pkt. </span>
                  {disc.message}
                </div>
              </div>
            ))}
            <div className="text-[11px] text-txt-secondary text-right mt-1 px-1">
              *Różnica ≥ 15 punktów jest statystycznie istotna (norma WISC-V) i automatycznie włączona do promptu AI.
            </div>
          </div>
        ) : (
          <div className="p-3 border border-accent-border bg-accent-soft rounded-xl text-xs text-txt-secondary flex items-center gap-2.5 shadow-[0_2px_10px_rgba(16,185,129,0.01)]">
            <div className="w-2 h-2 rounded-full bg-accent" />
            <div>
              <span className="font-semibold text-accent-strong">Profil zrównoważony.</span> Brak statystycznie istotnych rozbieżności (≥ 15 pkt) między wskaźnikami złożonymi.
            </div>
          </div>
        )}
      </div>

      {/* Percentiles List & Real-time Progress tracks */}
      <div className="border-t border-brd pt-4 flex flex-col gap-3">
        <h4 className="text-sm font-semibold text-txt-secondary">Wskaźniki i Ranga Centylowa</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(compositeNames).map(([key, name], cardIdx) => {
            const valStr = compositeScores[key];
            const score = parseInt(valStr);
            const isFilled = !isNaN(score) && score > 0;
            const pct = isFilled ? getPercentile(score, 'composite') : null;
            const interpretation = isFilled ? getScoreInterpretation(score, 'composite') : '';
            const color = domainColors[key] || '#EC4899';
            const fillWidth = isFilled ? ((score - minScore) / (maxScore - minScore)) * 100 : 0;

            return (
              <div
                key={key}
                className={`p-2.5 rounded-xl border ${
                  isFilled
                    ? 'bg-surface-card border-brd shadow-[0_2px_8px_rgba(0,0,0,0.02)]'
                    : 'bg-surface-subtle border-transparent opacity-40'
                }`}
                style={{
                  animation: cardsVisible ? `fadeSlideUp 0.35s ease-out ${cardIdx * 55}ms both` : 'none',
                }}
              >
                <div className="flex justify-between items-center gap-2 mb-1.5">
                  <span
                    className="text-xs font-semibold text-txt-secondary flex-shrink-0"
                    title={compositeNames[key] || name}
                  >
                    {shortCompositeNames[key] || name}
                  </span>
                  {isFilled && (
                    <span
                      className="text-[11px] px-1.5 py-0.5 rounded-full font-semibold tracking-wide shrink-0"
                      style={{ backgroundColor: domainColorAlpha(color, 9), color: domainColorsStrong[key] || color }}
                    >
                      {valStr}
                    </span>
                  )}
                </div>

                {isFilled && pct !== null ? (
                  <div className="flex flex-col gap-1.5">
                    {/* Animated progress track */}
                    <div className="w-full h-1.5 bg-surface-subtle rounded-full overflow-hidden relative">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: cardsVisible ? `${fillWidth}%` : '0%',
                          backgroundColor: color,
                          boxShadow: `0 0 8px ${domainColorAlpha(color, 31)}`,
                          transition: `width 0.9s cubic-bezier(0.4,0,0.2,1) ${cardIdx * 55 + 200}ms`,
                        }}
                      />
                      {/* Normal range overlay marker (85–115) */}
                      <div className="absolute top-0 bottom-0 left-[37.5%] right-[37.5%] border-l border-r border-brd bg-stone-500/5 pointer-events-none" />
                    </div>
                    {/* Percentile and label */}
                    <div className="flex justify-between text-[11px] text-txt-secondary">
                      <span>Centyl: <strong className="text-txt-primary">{pct}%</strong></span>
                      <span className="italic">{interpretation}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-txt-tertiary italic">Brak danych</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </CollapsibleCard>
  );
};

